// apps/client/src/ui/rotate.tsx (portrait landing, 2026-10-18; spec §8.5) — a touch phone held upright. The landing page works upright
// (ui/screens/title.tsx lays itself out for a tall screen); everything after Play now needs the phone sideways (state/slices/title.ts
// needsLandscape), so App shows this friendly card over the scene instead: a small phone turning sideways, "Turn your phone sideways to
// play", the Full screen button (ui/fullscreen.tsx) and, while Play now waits for the turn, a way back to the landing page. It replaced
// the plain "Turn your phone sideways" overlay of every screen.
import { useSyncExternalStore } from 'react';
import { useGame } from '../state/store';
import { FullscreenButton } from './fullscreen';
import { useT } from './i18n';

/** A touch screen taller than wide (a desktop window may be any shape: the game plays there as it is). */
export const PORTRAIT_QUERY = '(pointer: coarse) and (max-aspect-ratio: 1/1)';
export function isPortrait(): boolean {
  return typeof matchMedia === 'function' && matchMedia(PORTRAIT_QUERY).matches;
}
function subscribe(fn: () => void): () => void {
  const q = typeof matchMedia === 'function' ? matchMedia(PORTRAIT_QUERY) : null;
  q?.addEventListener?.('change', fn);
  addEventListener('resize', fn);
  return () => { q?.removeEventListener?.('change', fn); removeEventListener('resize', fn); };
}
/** Whether the phone is held upright now, following it as it turns. */
export function usePortrait(): boolean {
  return useSyncExternalStore(subscribe, isPortrait, () => false);
}

const CSS = `
.rotate-layer { position: fixed; inset: 0; z-index: 100; display: grid; place-items: center; box-sizing: border-box;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  background: radial-gradient(130% 75% at 50% 46%, rgba(255,251,241,.18) 0%, rgba(255,251,241,.42) 58%, rgba(255,251,241,.62) 100%);
  animation: rotate-in .35s var(--ease-out); }
.rotate-card { box-sizing: border-box; width: min(340px, 100%); max-height: 100%; overflow: auto; background: var(--cream-50);
  border-radius: var(--r-panel); box-shadow: var(--sh-card); padding: 20px 22px 18px; display: grid; justify-items: center; gap: 8px; text-align: center; }
.rotate-pic { width: 128px; height: 104px; margin: 2px 0 4px; }
.rotate-phone { transform-box: view-box; transform-origin: 64px 52px; transform: rotate(90deg); animation: rotate-phone 3.2s var(--ease-out) infinite; }
.rotate-card h2 { margin: 0; font-weight: 900; font-size: 24px; line-height: 1.12; letter-spacing: -0.01em; color: var(--wood-800); text-wrap: balance; }
.rotate-card p { margin: 0; font-weight: 700; font-size: 15px; line-height: 1.4; color: var(--wood-700); text-wrap: pretty; }
.rotate-actions { display: grid; gap: 4px; justify-items: center; margin-top: 8px; }
.rotate-actions:empty { display: none; }
.rotate-back { min-height: 44px; font-size: 15px; padding: 10px 18px 11px; background: transparent; box-shadow: none; color: var(--wood-700); }
.rotate-back:hover:not(:disabled) { background: var(--cream-100); }
/* Upright, then turning sideways, held there, and round again (with reduced motion it simply rests sideways). */
@keyframes rotate-phone {
  0%, 16% { transform: rotate(0deg); opacity: 1; }
  44%, 80% { transform: rotate(90deg); opacity: 1; }
  88% { transform: rotate(90deg); opacity: 0; }
  89% { transform: rotate(0deg); opacity: 0; }
  100% { transform: rotate(0deg); opacity: 1; }
}
@keyframes rotate-in { from { opacity: 0; } }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'rotate';
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** A phone in wood ink turning a quarter turn clockwise, the turn's arrow round it (DESIGN.md "Icons"). The little town on its screen
 *  is drawn for landscape, so it lies on its side while the phone is upright and rights itself once the phone is sideways. */
function RotatingPhone() {
  return (
    <svg className="rotate-pic" viewBox="0 0 128 104" aria-hidden="true" focusable="false">
      <path d="M81.1 5A50 50 0 0 1 113.2 43.3" fill="none" stroke="var(--leaf-700)" strokeWidth="5" strokeLinecap="round" />
      <path d="M117 35.2 113.2 43.3 106.9 37" fill="none" stroke="var(--leaf-700)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <clipPath id="rotate-screen"><rect x="50" y="23" width="28" height="58" rx="5" /></clipPath>
      <g className="rotate-phone">
        <rect x="45" y="16" width="38" height="72" rx="10" fill="var(--cream-50)" stroke="var(--wood-800)" strokeWidth="4" />
        <g clipPath="url(#rotate-screen)">
          <g transform="rotate(-90 64 52)">
            <rect x="35" y="38" width="58" height="28" fill="var(--sky-200)" />
            <ellipse cx="64" cy="71" rx="36" ry="11" fill="var(--leaf-400)" />
            <rect x="58" y="52" width="12" height="8.5" rx="1.6" fill="#FFF4E2" />
            <path d="M56.5 53.2 64 47l7.5 6.2z" fill="var(--coral-500)" stroke="var(--coral-500)" strokeWidth="1.8" strokeLinejoin="round" />
            <rect x="62.4" y="55.4" width="3.2" height="5.1" rx=".9" fill="#8A6A4F" />
            <circle cx="84" cy="44.5" r="3.2" fill="var(--sun-400)" />
          </g>
        </g>
      </g>
    </svg>
  );
}

/** Over every screen that needs landscape while the phone is held upright. */
export function RotateCard() {
  injectStyle();
  const t = useT();
  const waiting = useGame((s) => s.titleStep === 'turn');
  return (
    <div className="rotate-layer hit">
      <section className="rotate-card" role="dialog" aria-modal="true" aria-labelledby="rotate-h">
        <RotatingPhone />
        <h2 id="rotate-h">{t('fullscreen.rotateTitle')}</h2>
        <p>{t('fullscreen.rotateBody')}</p>
        <div className="rotate-actions">
          <FullscreenButton />
          {waiting && (
            <button type="button" className="btn rotate-back" onClick={() => useGame.getState().titleBack()}>{t('fullscreen.back')}</button>
          )}
        </div>
      </section>
    </div>
  );
}
